import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    UserAvatar,
} from '@t-one-internal/design-system'
import { User, Gear, SignOut, CaretDown, Lock, ShieldCheck } from '@phosphor-icons/react'
import { useAuth } from '../context/AuthContextBackend'
import { NotificationBell } from './notifications'
import { performLogout } from '../services/logoutService'
import { getLoginUrl } from '../utils/subdomain'
import TwoFactorSetupModal from './TwoFactorSetupModal'
import { getUserTwoFactorStatus, disableTwoFactorForUser } from '../services/twoFactorApi'

export const TopBar = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { user, account } = useAuth()
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const [cycleName, setCycleName] = useState('')

    // 2FA state
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
    const [twoFactorCanDisable, setTwoFactorCanDisable] = useState(true)
    const [isLoadingTwoFactor, setIsLoadingTwoFactor] = useState(false)
    const [isUpdatingTwoFactor, setIsUpdatingTwoFactor] = useState(false)
    const [isTwoFactorSetupModalOpen, setIsTwoFactorSetupModalOpen] = useState(false)

    // Fetch 2FA status on mount
    useEffect(() => {
        const fetchTwoFactorStatus = async () => {
            if (!user?.user_uuid) return
            setIsLoadingTwoFactor(true)
            try {
                const response = await getUserTwoFactorStatus(String(user.user_uuid))
                if (response?.data) {
                    setTwoFactorEnabled(response.data.is_2fa_enabled || (response.data.is_mfa_mandatory ?? false))
                    setTwoFactorCanDisable(
                        !(response.data.is_mfa_mandatory ?? false) && !response.data.enabled_by
                    )
                }
            } catch (error) {
                console.error('Failed to fetch 2FA status:', error)
            } finally {
                setIsLoadingTwoFactor(false)
            }
        }
        fetchTwoFactorStatus()
    }, [user?.user_uuid])

    const handleToggleTwoFactor = async () => {
        if (isUpdatingTwoFactor || isLoadingTwoFactor) return

        if (!twoFactorEnabled) {
            // Enable: open setup modal
            setIsTwoFactorSetupModalOpen(true)
            return
        }

        if (!twoFactorCanDisable) return

        // Disable 2FA
        if (!user?.user_uuid) return
        setIsUpdatingTwoFactor(true)
        try {
            await disableTwoFactorForUser(String(user.user_uuid), String(user.user_uuid))
            setTwoFactorEnabled(false)
        } catch (error) {
            console.error('Failed to disable 2FA:', error)
        } finally {
            setIsUpdatingTwoFactor(false)
        }
    }

    const handleTwoFactorSetupComplete = async () => {
        setIsTwoFactorSetupModalOpen(false)
        if (!user?.user_uuid) return
        try {
            const response = await getUserTwoFactorStatus(String(user.user_uuid))
            if (response?.data) {
                setTwoFactorEnabled(response.data.is_2fa_enabled || (response.data.is_mfa_mandatory ?? false))
                setTwoFactorCanDisable(
                    !(response.data.is_mfa_mandatory ?? false) && !response.data.enabled_by
                )
            }
        } catch (error) {
            console.error('Failed to refresh 2FA status:', error)
        }
    }

    // Generate breadcrumbs from path
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const isCollaboratorMode = localStorage.getItem('collaborator_mode') === 'true'

    // Check if we're in a workforce or organization route
    const isWorkforceRoute = pathSegments[0] === 'workforce'
    const isOrganizationRoute = pathSegments[0] === 'organization' || pathSegments[0] === 'organisation'
    const isReviewCloudRoute = pathSegments[0] === 'performspark'
    const isAnalyticsRoute = pathSegments[0] === 'analytics'
    const isAnalyticsReportsRoute = isAnalyticsRoute && pathSegments[1] === 'reports'
    const isIntegrationsRoute = pathSegments[0] === 'integrations'
    const isRecognitionsRoute = pathSegments[0] === 'my-space' && pathSegments[1] === 'recognitions'
    const isSpecialRoute = isWorkforceRoute || isOrganizationRoute || isReviewCloudRoute || isAnalyticsRoute || isIntegrationsRoute
    const isCompBuilderRoute = pathSegments[0] === 'comp-builder'

    // Custom label mappings for organization routes
    const organizationLabelMap: Record<string, string> = {
        'details': 'Profile',
        'roles': 'Access Management',
        'audit': 'Access Trail',
        'communications': 'Notification',
        'notifications': 'Notification Summary',
        'templates': 'Templates',
        'triggers': 'Triggers',
        'logs': 'Logs',
    }

    // Custom label mappings for review-cloud routes
    const reviewCloudModuleLabelMap: Record<string, string> = {
        'cycle-management': 'Cycles',
        'performance-management': 'Performance',
        'performance': 'Performance',
        'goal-management': 'Goals',
        'goals': 'Goals',
        'talent-management': 'Talent',
        'talent': 'Talent',
        'surveys': 'Surveys',
        'engagement-surveys': 'Surveys',
    }

    // Custom label mappings for reports routes
    const reportsLabelMap: Record<string, string> = {
        'perform-spark': 'Library',
        'overview': 'Overview',
        'configure': 'Configure',
        'view': 'View Report',
    }

    // Custom label mappings for specific page segments
    const pageLabelMap: Record<string, string> = {
        'my-portal': 'My Portal',
        'team-dashboard': 'Team Dashboard',
        'unit-hr': 'Unit HR',
        'unit-exec': 'Unit Exec',
        'org-hr': 'Org HR',
        'comp-builder': 'CompBldr',
        'stmt-builder': 'Total Rewards',
        'job-builder': 'JobBldr',
        'library': 'Library',
        'report-builder': 'Custom Reports',
        'titles': 'Titles',
        'job-evaluation': 'Job Evaluation',
        'gradeboard': 'GradeBoard',
        'studio': 'JESAP Studio',
        'job-grade': 'Job Grade',
        'acknowledgement': 'Delivery',
        'review360': '360 Review',
        'pip-management': 'PIP Management',
        'idp': 'IDP',
        'builder': 'Goal Builder',
        'cycles': 'Cycle Summary',
        'pay-builder': 'Comp Planner',
        'comp-cycles': 'My Cycles',
        'title-summary': 'Salary Ranges',
        'tracker': 'Goal Tracker',
        'create': 'Create',
        'templates': 'Templates',
        'questions': 'Questions',
        'new': 'Create',
        'edit': 'Edit',
        'learning-operations': 'TraineryOps',
        'e-learning': 'LMS',
    }

    // Listen for cycle-name pushes from comp-builder pages
    useEffect(() => {
        const handler = (e: Event) => setCycleName((e as CustomEvent).detail?.name || '')
        window.addEventListener('comp-builder:set-cycle-name', handler)
        return () => window.removeEventListener('comp-builder:set-cycle-name', handler)
    }, [])

    // Read stored cycle name on every route change (covers hard-refreshes / back-button)
    useEffect(() => {
        if (isCompBuilderRoute) {
            let cyclesIdx = pathSegments.indexOf('cycles')
            if (cyclesIdx === -1) cyclesIdx = pathSegments.indexOf('comp-cycles')
            const id = cyclesIdx !== -1 && cyclesIdx + 1 < pathSegments.length ? pathSegments[cyclesIdx + 1] : null
            setCycleName(id ? (sessionStorage.getItem(`comp-builder-cycle-name-${id}`) || '') : '')
        } else {
            setCycleName('')
        }
    }, [location.pathname, isCompBuilderRoute, pathSegments])

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false)
            }
        }

        if (isUserMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isUserMenuOpen])

    const handleLogout = async () => {
        try {
            setIsUserMenuOpen(false)
            await performLogout()
        } catch (error) {
            console.error('Logout failed:', error)
            window.location.href = getLoginUrl()
        }
    }

    const getUserName = () => {
        if (!user) return 'Guest User'
        if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`
        if (user.first_name) return user.first_name
        if (user.email) return user.email.split('@')[0]
        return 'Guest User'
    }

    return (
        <div className="app-topbar-container h-16 border-b border-gray-200 bg-white px-6 flex items-center justify-between z-[70] relative">
            <div className="flex items-center gap-4">
                <Breadcrumb>
                    <BreadcrumbList>
                        {!isSpecialRoute && !isCollaboratorMode && !isRecognitionsRoute && (
                            <BreadcrumbItem>
                                <BreadcrumbLink href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                    e.preventDefault()
                                    navigate('/')
                                }} className="text-gray-500 hover:text-gray-900">Home</BreadcrumbLink>
                            </BreadcrumbItem>
                        )}
                        {/* Recognitions breadcrumb: Recognitions / Recognition Feed or Leaderboard */}
                        {isRecognitionsRoute && (() => {
                            const tab = new URLSearchParams(location.search).get('tab') || 'feed'
                            const tabLabel = tab === 'leaderboard' ? 'Leaderboard' : 'Recognition Feed'
                            return (
                                <>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                            e.preventDefault()
                                            navigate('/my-space/recognitions')
                                        }} className="text-gray-500 hover:text-gray-900">Recognitions</BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator className="mx-1">
                                        <span className="text-gray-300">/</span>
                                    </BreadcrumbSeparator>
                                    <BreadcrumbItem>
                                        <BreadcrumbPage>{tabLabel}</BreadcrumbPage>
                                    </BreadcrumbItem>
                                </>
                            )
                        })()}
                        {/* Add Analytics prefix for analytics routes */}
                        {isAnalyticsRoute && (
                            <>
                                {pathSegments.length === 1 ? (
                                    <BreadcrumbItem>
                                        <BreadcrumbPage>Analytics</BreadcrumbPage>
                                    </BreadcrumbItem>
                                ) : (
                                    <BreadcrumbItem>
                                        <BreadcrumbLink href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                            e.preventDefault()
                                            navigate('/analytics/dashboards')
                                        }} className="text-gray-500 hover:text-gray-900">Analytics</BreadcrumbLink>
                                    </BreadcrumbItem>
                                )}
                                {/* Add Reports sub-prefix for analytics/reports routes */}
                                {isAnalyticsReportsRoute && pathSegments.length > 2 && (
                                    <>
                                        <BreadcrumbSeparator className="mx-1">
                                            <span className="text-gray-300">/</span>
                                        </BreadcrumbSeparator>
                                        <BreadcrumbItem>
                                            <BreadcrumbLink href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                                e.preventDefault()
                                                navigate('/analytics/reports')
                                            }} className="text-gray-500 hover:text-gray-900">Reports</BreadcrumbLink>
                                        </BreadcrumbItem>
                                    </>
                                )}
                            </>
                        )}
                        {pathSegments.map((segment, index) => {
                            // Skip all segments for recognitions route (handled by custom breadcrumb above)
                            if (isRecognitionsRoute) {
                                return null
                            }

                            // Skip "performspark" segment for PerformSpark routes
                            if (isReviewCloudRoute && segment === 'performspark') {
                                return null
                            }

                            // Skip "analytics" segment for analytics routes (handled by prefix above)
                            if (isAnalyticsRoute && segment === 'analytics') {
                                return null
                            }

                            // Skip "reports" segment for analytics/reports routes (handled by prefix above)
                            if (isAnalyticsReportsRoute && segment === 'reports') {
                                return null
                            }

                            // Skip "my-space" and "home" segments (Home is rendered as prefix)
                            if (segment === 'my-space' || segment === 'home') {
                                return null
                            }

                            // Skip numeric-only segments (like goal IDs) and UUID-style IDs
                            if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
                                // Exception: keep the cycle ID in comp-builder (it sits right after 'cycles' or 'comp-cycles')
                                const compCyclesIdx = pathSegments.indexOf('cycles')
                                const compCyclesIdx2 = pathSegments.indexOf('comp-cycles')
                                const effectiveCyclesIdx = compCyclesIdx !== -1 ? compCyclesIdx : compCyclesIdx2
                                if (!(isCompBuilderRoute && effectiveCyclesIdx !== -1 && index === effectiveCyclesIdx + 1)) {
                                    return null
                                }
                            }

                            // Skip any segment that comes after 'cycles', 'cycle', 'employee', 'details', or 'goal'
                            // EXCEPT for known page segments like 'create', 'edit', etc.
                            const cyclesIndex = pathSegments.indexOf('cycles')
                            const compCyclesIndex = pathSegments.indexOf('comp-cycles')
                            const effectiveCyclesIndex = cyclesIndex !== -1 ? cyclesIndex : compCyclesIndex
                            const cycleIndex = pathSegments.indexOf('cycle')
                            const employeeIndex = pathSegments.indexOf('employee')
                            const detailsIndex = pathSegments.indexOf('details')
                            const goalIndex = pathSegments.indexOf('goal')
                            const knownPageSegments = ['create', 'edit', 'builder', 'tracker', 'overview', 'employees']

                            // comp-builder: on cycles/overview (list page), hide 'cycles' and 'overview' since they duplicate Comp Planner
                            // but on cycles/:id/overview (detail page), only hide 'overview'
                            if (isCompBuilderRoute && pathSegments.includes('cycles') && pathSegments[pathSegments.length - 1] === 'overview' && !pathSegments.some(s => /^\d+$/.test(s)) && (segment === 'cycles' || segment === 'overview')) {
                                return null
                            }
                            if (isCompBuilderRoute && pathSegments.includes('cycles') && pathSegments[pathSegments.length - 1] === 'overview' && pathSegments.some(s => /^\d+$/.test(s)) && segment === 'overview') {
                                return null
                            }

                            // comp-builder: on cycles/list, hide 'list' and make 'cycles' point to /cycles/list
                            if (isCompBuilderRoute && pathSegments.includes('cycles') && pathSegments[pathSegments.length - 1] === 'list' && segment === 'list') {
                                return null
                            }

                            // comp-builder: on cycles/settings (global settings), hide 'cycles' so breadcrumb shows only Settings
                            if (isCompBuilderRoute && pathSegments.includes('settings') && !pathSegments.some(s => /^\d+$/.test(s)) && segment === 'cycles') {
                                return null
                            }

                            // (cycle ID under cycles is now shown as cycle name for employees, tracker, settings pages)

                            // For comp-builder, drop 'worksheet' and everything after it
                            // Exception: show 'by-manager' as "Department" breadcrumb
                            if (isCompBuilderRoute) {
                                const worksheetIdx = pathSegments.indexOf('worksheet')
                                const hasDeptRoute = pathSegments.includes('by-manager') || pathSegments.includes('manager')
                                if (worksheetIdx !== -1 && index >= worksheetIdx) {
                                    if (hasDeptRoute && (segment === 'by-manager' || segment === 'manager')) {
                                        // will be rendered as "Department" below
                                    } else {
                                        return null
                                    }
                                }
                                const settingsIdx = pathSegments.indexOf('settings')
                                if (cyclesIndex !== -1 && settingsIdx !== -1 && index > settingsIdx) {
                                    return null
                                }
                            }

                            // For cycles/cycle, allow known page segments after them
                            if ((cyclesIndex !== -1 && index > cyclesIndex) || (cycleIndex !== -1 && index > cycleIndex)) {
                                if (!knownPageSegments.includes(segment)) {
                                    // comp-builder: also allow the cycle-ID slot, 'worksheet', and cycle-level 'settings'
                                    if (!(isCompBuilderRoute && cyclesIndex !== -1 &&
                                        (index === cyclesIndex + 1 || segment === 'worksheet' || segment === 'settings'))) {
                                        return null
                                    }
                                }
                            }
                            // For employee/details/goal, skip all segments after them (they're followed by IDs)
                            if ((employeeIndex !== -1 && index > employeeIndex) ||
                                (detailsIndex !== -1 && index > detailsIndex) ||
                                (goalIndex !== -1 && index > goalIndex)) {
                                return null
                            }

                            const isLast = index === pathSegments.length - 1

                            // Build path - for detailed-survey, include the ID if it exists
                            let pathSegmentsForLink = pathSegments.slice(0, index + 1)
                            if (segment === 'detailed-survey' && index + 1 < pathSegments.length) {
                                const nextSegment = pathSegments[index + 1]
                                // If next segment looks like an ID (numeric or UUID), include it
                                if (/^\d+$/.test(nextSegment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(nextSegment)) {
                                    pathSegmentsForLink = pathSegments.slice(0, index + 2)
                                }
                            }

                            // comp-builder: For cycle ID breadcrumb, navigate to worksheet instead of dashboard
                            let path = `/${pathSegmentsForLink.join('/')}`
                            if (isCompBuilderRoute && segment === 'pay-builder' && pathSegments.includes('cycles')) {
                                path = `${path}/cycles/overview`
                            } else if (isCompBuilderRoute && segment === 'cycles' && pathSegments[pathSegments.length - 1] === 'list') {
                                path = `${path}/list`
                            } else if (isCompBuilderRoute && segment === 'cycles' && (pathSegments.includes('employees') || pathSegments.includes('tracker') || (pathSegments.includes('settings') && pathSegments.some(s => /^\d+$/.test(s))) || (pathSegments[pathSegments.length - 1] === 'overview' && pathSegments.some(s => /^\d+$/.test(s))))) {
                                path = `${path}/list`
                            } else if (isCompBuilderRoute && effectiveCyclesIndex !== -1 && index === effectiveCyclesIndex + 1 && /^\d+$/.test(segment)) {
                                if (compCyclesIndex !== -1) {
                                    path = `${path}/worksheet/all-employees`
                                } else {
                                    path = `${path}/overview`
                                }
                            }

                            // Format segment: replace dashes with spaces, capitalize
                            let label = segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

                            // Apply custom labels for organization routes
                            if (isOrganizationRoute && index > 0 && organizationLabelMap[segment]) {
                                label = organizationLabelMap[segment]
                            }

                            // Apply custom labels for review-cloud routes
                            if (isReviewCloudRoute && index === 1 && reviewCloudModuleLabelMap[segment]) {
                                label = reviewCloudModuleLabelMap[segment]
                            }

                            // Apply custom labels for analytics/reports routes
                            if (isAnalyticsReportsRoute && index >= 2 && reportsLabelMap[segment]) {
                                label = reportsLabelMap[segment]
                            }

                            // Apply custom page labels for any route
                            if (pageLabelMap[segment]) {
                                label = pageLabelMap[segment]
                            }

                            // comp-builder: override labels
                            if (isCompBuilderRoute && segment === 'tracker') {
                                label = 'Tracker'
                            }
                            if (isCompBuilderRoute && segment === 'employees') {
                                label = 'Employees'
                            }
                            if (isCompBuilderRoute && segment === 'settings' && pathSegments.some(s => /^\d+$/.test(s))) {
                                label = 'Cycle Setup'
                            }
                            if (isCompBuilderRoute && (segment === 'by-manager' || segment === 'manager')) {
                                label = 'Department'
                            }
                            if (segment === 'cycles' && isCompBuilderRoute) {
                                label = 'Cycles'
                            }

                            // Context-aware label for 'cycle' segment based on parent route
                            if (segment === 'cycle' && isReviewCloudRoute) {
                                if (pathSegments[1] === 'goal-management') {
                                    label = 'Goal Cycles'
                                } else if (pathSegments[1] === 'cycle-management') {
                                    label = 'Review Cycles'
                                }
                            }

                            // Context-aware label for 'library' segment - show "Template Library" under cycle-management
                            if (segment === 'library' && isReviewCloudRoute && pathSegments[1] === 'cycle-management') {
                                label = 'Template Library'
                            }

                            // Override 'Customers' with custom label from account settings
                            if (segment === 'customers' && account?.customer_label) {
                                label = account.customer_label
                            }

                            // comp-builder: replace the raw cycle-ID segment with the actual cycle name
                            if (isCompBuilderRoute && effectiveCyclesIndex !== -1 && index === effectiveCyclesIndex + 1) {
                                if (cycleName) {
                                    label = cycleName
                                } else if (/^\d+$/.test(segment)) {
                                    label = `Cycle ${segment}`
                                }
                                // else: keep the default dash-capitalised label (e.g. cycle-3 → Cycle 3)
                            }

                            // For special routes, show the second segment or default label
                            if (isSpecialRoute && index === 0 && pathSegments.length === 1) {
                                // Root workforce or organization route - show main section with default subsection
                                if (isWorkforceRoute) {
                                    label = 'Workforce'
                                } else if (isOrganizationRoute) {
                                    label = 'Organization'
                                }
                            }

                            // Check if this is truly the last item to display
                            // If we're on a root special route (length === 1), we'll add a default subsection after, so this isn't truly last
                            // Also, if we're on a review-cloud route with 2 segments, we'll add "Overview" after, so the module name isn't truly last
                            // Special case for cycles/cycle: only treat as last if there's no known page segment after it
                            const hasKnownSegmentAfterCycle = (segment === 'cycles' || segment === 'cycle' || segment === 'comp-cycles') &&
                                (pathSegments.slice(index + 1).some(s => knownPageSegments.includes(s)) ||
                                    // comp-builder: a cycle ID after 'cycles'/'comp-cycles' counts as a visible segment
                                    (isCompBuilderRoute && index + 1 < pathSegments.length && /^\d+$/.test(pathSegments[index + 1])))
                            // Special case: 'employee', 'details', 'goal' should always be treated as last (non-clickable)
                            const isLastVisibleSegment = segment === 'employee' || segment === 'details' || segment === 'goal' ||
                                ((segment === 'cycles' || segment === 'cycle' || segment === 'comp-cycles') && !hasKnownSegmentAfterCycle) ||
                                // comp-builder: cycle ID is last unless a known page breadcrumb follows
                                (isCompBuilderRoute && effectiveCyclesIndex !== -1 && index === effectiveCyclesIndex + 1 && !(pathSegments.includes('by-manager') || pathSegments.includes('manager') || pathSegments.includes('employees') || pathSegments.includes('tracker') || (pathSegments.includes('settings') && cyclesIndex !== -1))) ||
                                // comp-builder: cycle-level 'settings' is the last visible crumb on settings pages
                                (isCompBuilderRoute && segment === 'settings' && effectiveCyclesIndex !== -1 && index > effectiveCyclesIndex) ||
                                (isLast && !(isSpecialRoute && pathSegments.length === 1) && !(isReviewCloudRoute && pathSegments.length === 2))
                            const isTrulyLast = isLastVisibleSegment

                            // Check if this is the first visible segment for special routes
                            const isFirstVisibleSpecialSegment = isSpecialRoute && (
                                (isReviewCloudRoute && index === 1) ||
                                (!isReviewCloudRoute && index === 0)
                            )

                            return (
                                <React.Fragment key={path}>
                                    {/* Add separator: for special routes, only if not the first visible item */}
                                    {((isReviewCloudRoute && index > 1) || (isAnalyticsReportsRoute && index >= 2) || (isAnalyticsRoute && !isAnalyticsReportsRoute && index > 0) || (isSpecialRoute && !isReviewCloudRoute && !isAnalyticsRoute && index > 0) || !isSpecialRoute) && (
                                        <BreadcrumbSeparator className="mx-1">
                                            <span className="text-gray-300">/</span>
                                        </BreadcrumbSeparator>
                                    )}
                                    <BreadcrumbItem>
                                        {/* comp-builder: keep cycle summary and cycle-name/id breadcrumbs clickable */}
                                        {((isTrulyLast &&
                                            !(isCompBuilderRoute && segment === 'cycles') &&
                                            !(isCompBuilderRoute && segment === 'comp-cycles') &&
                                            !(isCompBuilderRoute && effectiveCyclesIndex !== -1 && index === effectiveCyclesIndex + 1)) ||
                                            (isCompBuilderRoute && segment === 'comp-builder')) ? (
                                            <BreadcrumbPage>{label}</BreadcrumbPage>
                                        ) : (
                                            <BreadcrumbLink href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                                e.preventDefault()
                                                navigate(path)
                                            }} className="text-gray-500 hover:text-gray-900">
                                                {label}
                                            </BreadcrumbLink>
                                        )}
                                    </BreadcrumbItem>
                                </React.Fragment>
                            )
                        })}
                        {/* Add default subsection label for root special routes (excluding analytics, handled in prefix) */}
                        {isSpecialRoute && !isAnalyticsRoute && pathSegments.length === 1 && (
                            <>
                                <BreadcrumbSeparator className="mx-1">
                                    <span className="text-gray-300">/</span>
                                </BreadcrumbSeparator>
                                <BreadcrumbItem>
                                    <BreadcrumbPage>
                                        {isWorkforceRoute ? 'Overview' : isOrganizationRoute ? 'Profile' : 'Overview'}
                                    </BreadcrumbPage>
                                </BreadcrumbItem>
                            </>
                        )}
                        {/* Add default subsection label for review-cloud routes with only 2 segments */}
                        {isReviewCloudRoute && pathSegments.length === 2 && (
                            <>
                                <BreadcrumbSeparator className="mx-1">
                                    <span className="text-gray-300">/</span>
                                </BreadcrumbSeparator>
                                <BreadcrumbItem>
                                    <BreadcrumbPage>Overview</BreadcrumbPage>
                                </BreadcrumbItem>
                            </>
                        )}
                        {/* Add tab-based subsection for surveys/library route */}
                        {isReviewCloudRoute && pathSegments[1] === 'surveys' && pathSegments[2] === 'library' && pathSegments.length === 3 && (
                            <>
                                <BreadcrumbSeparator className="mx-1">
                                    <span className="text-gray-300">/</span>
                                </BreadcrumbSeparator>
                                <BreadcrumbItem>
                                    <BreadcrumbPage>
                                        {new URLSearchParams(location.search).get('tab') === 'questions' ? 'Questions' : 'Templates'}
                                    </BreadcrumbPage>
                                </BreadcrumbItem>
                            </>
                        )}
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="flex items-center gap-6">
                <NotificationBell />
                {/* User Profile */}
                <div className="relative" ref={menuRef}>
                    <button
                        className="flex items-center gap-2 hover:bg-gray-50 p-1.5 pr-2 rounded-full transition-colors border border-transparent hover:border-gray-200"
                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    >
                        <UserAvatar
                            name={getUserName()}
                            size="lg"
                            imageUrl={user?.profile_pic_url}
                        />
                        <CaretDown weight="bold" className={`text-gray-400 text-xs transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isUserMenuOpen && (
                        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-100 z-[80]">
                            <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                                <p className="text-sm font-semibold text-gray-900 truncate">{getUserName()}</p>
                                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                            </div>

                            {/* Profile option removed - accessible from sidebar */}
                            {/* <div className="p-1">
                                <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors text-left"
                                    onClick={() => {
                                        setIsUserMenuOpen(false)
                                        navigate('/my-profile')
                                    }}
                                >
                                    <User size={16} />
                                    <span>Profile</span>
                                </button>
                            </div> */}

                            <div className="p-1">
                                <button
                                    onClick={() => {
                                        setIsUserMenuOpen(false)
                                        navigate('/change-password')
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors text-left font-medium"
                                >
                                    <Lock size={16} />
                                    <span>Change Password</span>
                                </button>
                            </div>

                            <div className="p-1">
                                <div
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 rounded-md transition-colors ${
                                        !twoFactorCanDisable && twoFactorEnabled ? 'opacity-60' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck size={16} />
                                        <span className="font-medium">Two-Factor Auth</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleToggleTwoFactor}
                                        disabled={isUpdatingTwoFactor || isLoadingTwoFactor || (!twoFactorCanDisable && twoFactorEnabled)}
                                        aria-pressed={twoFactorEnabled}
                                        aria-label="Toggle two-factor authentication"
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                            twoFactorEnabled ? 'bg-blue-600' : 'bg-gray-200'
                                        } ${(isUpdatingTwoFactor || isLoadingTwoFactor || (!twoFactorCanDisable && twoFactorEnabled)) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                twoFactorEnabled ? 'translate-x-4' : 'translate-x-0.5'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="p-1">
                                <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors text-left font-medium"
                                    onClick={handleLogout}
                                >
                                    <SignOut size={16} />
                                    <span>Logout</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <TwoFactorSetupModal
                isOpen={isTwoFactorSetupModalOpen}
                onClose={() => setIsTwoFactorSetupModalOpen(false)}
                onSetupComplete={handleTwoFactorSetupComplete}
                userEmail={user?.email}
            />
        </div>
    )
}
